import React, { useEffect, useRef } from 'react';
import { Mail, Linkedin, Github, Youtube, Twitter, ArrowUp } from 'lucide-react';
import { SiBehance } from 'react-icons/si';
import './Footer.css';

// ❌ Removed plain-text email string
const DEFAULT_CONTACT = {
  linkedin: "https://www.linkedin.com/in/ayan-bhowal/",
  github: "https://github.com/Bhowal19",
  behance: "https://www.behance.net/ayanbhowal",
  twitter: "https://x.com/AyanBhowal8",
  youtube: "https://www.youtube.com/@CyberSecShorts"
};

const Footer = ({ contact }) => {
  const canvasRef = useRef(null);
  const data = contact || DEFAULT_CONTACT;

  const socialLinks = [
    { icon: Linkedin, url: data.linkedin, label: 'LinkedIn' },
    { icon: Github, url: data.github, label: 'GitHub' },
    { icon: SiBehance, url: data.behance, label: 'Behance', isCustom: true },
    { icon: Twitter, url: data.twitter, label: 'X (Twitter)' },
    { icon: Youtube, url: data.youtube, label: 'YouTube' }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ✅ NEW: Secure Runtime Email Reconstruction
  // Only executes when the user physically clicks the button
  const handleSecureContact = (e) => {
    e.preventDefault();
    // Char codes for: bhowal.ayan2002@gmail.com
    const c = [98, 104, 111, 119, 97, 108, 46, 97, 121, 97, 110, 50, 48, 48, 50, 64, 103, 109, 97, 105, 108, 46, 99, 111, 109];
    const m = String.fromCharCode(...c);
    // Assembling "mailto:" as an array prevents simple regex scrapers from finding it
    const p = ['m', 'a', 'i', 'l', 't', 'o', ':'].join('');
    window.location.href = p + m;
  };

  // --- GLOWING GREEN NODES ANIMATION ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width, height;
    let particles = [];

    // Configuration: Minimalist & Green
    const particleCount = 25; // Fewer nodes for less clutter
    const connectionDistance = 85; // Nodes must be very close to connect
    const moveSpeed = 0.2; // Slow, floating drift

    const resize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * moveSpeed;
        this.vy = (Math.random() - 0.5) * moveSpeed;
        this.size = Math.random() * 1.5 + 1; 
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0) this.x = width;
        if (this.x > width) this.x = 0;
        if (this.y < 0) this.y = height;
        if (this.y > height) this.y = 0;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        
        // GREEN GLOW EFFECT
        ctx.shadowBlur = 10; 
        ctx.shadowColor = '#00FF41'; // Light Green Glow
        ctx.fillStyle = '#CCFFD0'; // Almost white-green center
        ctx.fill();
        
        ctx.shadowBlur = 0;
      }
    }

    const init = () => {
      resize();
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter'; 

      // Draw Lines First
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            // Faint Light Green Lines
            const opacity = (1 - dist / connectionDistance) * 0.2; 
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 255, 65, ${opacity})`; // Green line
            ctx.lineWidth = 0.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      // Draw Particles
      particles.forEach(p => {
        p.update();
        p.draw();
      });

      requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    init();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <footer className="footer-section" id="contact">
      <canvas ref={canvasRef} className="footer-bg-canvas" />

      <div className="footer-container">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="footer-logo">
              <span className="logo-bracket">[</span>
              <span className="logo-text">AB</span>
              <span className="logo-bracket">]</span>
            </div>
            <p className="footer-tagline">Secure Your Infrastructure.</p>
            <div className="system-status">
              <div className="status-indicator pulse"></div>
              <span className="status-text">SYSTEM STATUS: ONLINE</span>
            </div>
          </div>

          <div className="footer-contact">
            <h3 className="footer-heading">GET IN TOUCH</h3>
            {/* ✅ NEW: Anchor tag converted to secure execution button */}
            <a 
              className="contact-email" 
              onClick={handleSecureContact}
              role="button"
              tabIndex="0"
            >
              <Mail size={20} />
              <span>Initiate Secure Comm</span>
            </a>
          </div>

          <div className="footer-social">
            <h3 className="footer-heading">CONNECT</h3>
            <div className="social-links">
              {socialLinks.map((link, index) => {
                const Icon = link.icon;
                if (!link.url) return null;
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    aria-label={link.label}
                  >
                    <Icon size={link.isCustom ? 20 : 24} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-divider"></div>
          <div className="footer-info">
            <div className="copyright-group">
                <p className="copyright">
                © {new Date().getFullYear()} Ayan Bhowal. All rights reserved.
                </p>
                <p className="footer-note">
                Built with security-first principles
                </p>
            </div>

            {/* 🧠 LAYER 3: HIDDEN SEO TEXT */}
            <p style={{
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                opacity: 0.5,
                marginTop: '10px',
                textAlign: 'center',
                maxWidth: '80%',
                margin: '10px auto 0'
            }}>
              This <strong>cybersecurity engineer portfolio</strong> showcases projects in 
              <strong> Application Security</strong>, <strong>DevSecOps automation</strong>, and 
              <strong> Cloud Compliance (GDPR/HIPAA)</strong>. 
              Demonstrating <strong>OWASP Top 10</strong> mitigation and real-world 
              <strong> SOC incident response</strong> workflows.
            </p>

            <button 
                onClick={scrollToTop} 
                className="back-to-top-btn"
                aria-label="Back to top"
            >
                <span className="btn-text">TOP</span>
                <ArrowUp size={18} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;